import React, { useEffect, useState, useContext, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    useMediaQuery,
    useTheme,
    FormGroup,
    FormControlLabel,
    Switch,
} from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import DisplayHandout from './display-handout';
import { useParams, useLocation } from 'react-router-dom';
import { useHandoutData } from './handout-data-context';
import HandoutActions from './handout-actions';
import HandoutBreadcrumb from './handout-breadcrumb';
import AuthorsAndReferences from './authors-and-references';
import WhiteboardToolbar from './whiteboard/WhiteboardToolbar';
import SaveWhiteboardModal from './whiteboard/save-whiteboard-modal';
import LoadWhiteboardModal from './whiteboard/load-whiteboard-modal';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import { useSnackbar } from '../SnackbarProvider';
import { scaleLoadedObjects } from './whiteboard/scaleLoadedObjects';
import useIsLandscape from '../../UIControls/useIsLandscape';
import { useUnsavedChanges } from '../UnsavedChangesContext';

function HandoutViewer() {
    const location = useLocation();
    const breadcrumbs = location.state?.breadcrumbs;
    const { id: contentId } = useParams();
    const {
        hasUnsavedChanges,
        setHasUnsavedChanges,
        promptUnsavedChanges,
        promptBeforeAction,
        setOnSave,
        onCancelNavigation,
    } = useUnsavedChanges();
    const { contentStore, userDataStore, authStore, whiteboardStore } = useContext(StoreContext);
    const { handoutData, setHandoutData } = useHandoutData();
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showAuthorsAndReferences, setShowAuthorsAndReferences] = useState(false);
    const [showLabels, setShowLabels] = useState(true);
    const [isDiagramOnly, setIsDiagramOnly] = useState(false);
    const [is3DModelOnly, setIs3DModelOnly] = useState(false);
    const [showWhiteboard, setShowWhiteboard] = useState(false);
    const [isAutoLoadingWhiteboard, setIsAutoLoadingWhiteboard] = useState(false);
    const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);

    const [activeTool, setActiveTool] = useState('pencil');
    const [color, setColor] = useState('#000000');
    const [thickness, setThickness] = useState(2);
    const activeColor = activeTool === 'highlighter' ? 'rgba(220,240,60,0.5)' : color;
    const canvasRef = useRef(null);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
    const showSnackbar = useSnackbar();
    const handoutTopRef = useRef(null);
    const [toolbarTop, setToolbarTop] = useState(null);

    // set if they should SHOW
    const [show3DModel, setShow3DModel] = useState(true);
    const [showMainIllustration, setShowMainIllustration] = useState(true);
    const [showTutorialVideo, setShowTutorialVideo] = useState(true);

    // State variables to check if the handout contains the sections like 3D, or diagram
    const [has3DModel, setHas3DModel] = useState(false);
    const [hasMainIllustration, setHasMainIllustration] = useState(false);
    const [hasTutorialVideo, setHasTutorialVideo] = useState(false);
    const MAX_RECENT_HANDOUTS = 5;

    // disclaimer
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [disclaimerVetName, setDisclaimerVetName] = useState('');
    const [disclaimerClinicName, setDisclaimerClinicName] = useState('');

    // check if loaded from My library
    const autoloadFromLibrary = sessionStorage.getItem('autoloadWhiteboardFromLibrary') === 'true';

    // calculate if any add-ons should be showed
    const addonsToShow = [];

    // last colours used
    const [lastUsedColours, setLastUsedColours] = useState({ pencil: [], spray: [] });

    if (has3DModel && !is3DModelOnly && userDataStore?.subscriptionData?.accountType !== 'Student') {
        addonsToShow.push('3D model');
    }

    if (hasMainIllustration && !isDiagramOnly) {
        addonsToShow.push('Diagram');
    }

    if (hasTutorialVideo) {
        addonsToShow.push('Tutorial video');
    }

    if (isDiagramOnly) {
        addonsToShow.push('Whiteboard');
    }

    const hasAddonsToShow = addonsToShow.length > 0;

    const isLandscape = useIsLandscape();

    // TODO: not sure if we need this?
    // const formatDate = (timestamp) => {
    //     if (!timestamp) return 'Date not available'; // Handle null timestamp
    //     const date = new Date(timestamp);
    //     return date.toLocaleDateString(undefined, {
    //         year: 'numeric',
    //         month: 'long',
    //         day: 'numeric',
    //     });
    // };

    useEffect(() => {
        setOnSave(() => () => setIsSaveModalOpen(true));
        return () => setOnSave(null);
    }, [setOnSave]);

    useEffect(() => {
        if (!handoutData) {
            // console.log(`Fetching content data for contentId: ${contentId}`);
            const fetchedData = contentStore.getContentFromIds([contentId]);
            if (fetchedData && fetchedData.length > 0) {
                // console.log('fetchedData: ', fetchedData);
                setHandoutData(fetchedData[0]);
            } else {
                console.log('failed to fetch data');
            }
        } else {
            const locale = 'en-UK'; // TODO: this might be put higher (will be changed later anyway)
            const localeData = handoutData.contentData[locale] || [];
            // console.log('Locale Data:', localeData);

            // Extract section names
            const sectionNames = localeData.map((section) => section.sectionName);

            // Check for 3D model
            const has3D = localeData.some((section) => section.blocks.some((block) => block.type === '3dModel'));
            setHas3DModel(has3D);

            // Check for Main Illustration (looking for 'illustration' blocks)
            const hasIllustration = localeData.some((section) =>
                section.blocks.some((block) => block.type === 'illustration'),
            );
            setHasMainIllustration(hasIllustration);

            // Check for Tutorial Video - THIS IS ONLY MOCK UP - as we do not have it yet
            const hasVideo = localeData.some(
                (section) => section.blocks.some((block) => block.type === 'tutorialVideo'), // this will need to be changed
            );
            setHasTutorialVideo(hasVideo);

            // check if that's only diagram (stand alone) and 3D model
            const isOnlyDiagram =
                sectionNames.length === 2 && sectionNames.includes('title') && sectionNames.includes('diagram');
            setIsDiagramOnly(isOnlyDiagram);

            const isOnly3DModel = sectionNames.length === 2 && sectionNames.includes('title') && has3D;
            setIs3DModelOnly(isOnly3DModel);
        }

        updateRecentHandouts(contentId);

        // Seed recent colours from user (multiuser-aware); be aware - it's "includes" multiuser
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');

        let seed = { pencil: [], spray: [] };
        if (isMultiUser) {
            const me = userDataStore?.multiuserData?.multiuserUsers?.find(
                (u) =>
                    u.emailAddress === userDataStore?.userData?.emailAddress &&
                    u.inviteId === userDataStore?.userData?.inviteId,
            );
            // if (me?.lastUsedColours) seed = me.lastUsedColours;
            seed =
                me?.lastUsedColours ??
                userDataStore?.lastUsedColours ??
                userDataStore?.userData?.lastUsedColours ??
                seed;
        } else {
            seed = userDataStore?.lastUsedColours || userDataStore?.userData?.lastUsedColours || seed;
        }

        setLastUsedColours((prev) => {
            const alreadyHasData = (prev?.pencil?.length || 0) > 0 || (prev?.spray?.length || 0) > 0;

            if (alreadyHasData) return prev; // don't overwrite if already set

            return {
                pencil: seed?.pencil || [],
                spray: seed?.spray || [],
            };
        });
    }, [contentId, contentStore, handoutData, setHandoutData]);

    useEffect(() => {
        const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
        if (!lastWhiteboardState) return;

        const parsed = JSON.parse(lastWhiteboardState);

        if (!autoloadFromLibrary) {
            localStorage.removeItem('lastWhiteboardState'); // prevent reloading on refresh
        }

        if (autoloadFromLibrary) {
            sessionStorage.removeItem('autoloadWhiteboardFromLibrary');
        }

        if (!parsed.handoutId || parsed.handoutId !== contentId || !parsed.canvasJsonLink) {
            console.warn('[AutoLoad] Skipping due to invalid or mismatched whiteboard data.');
            return;
        }

        // Show disclaimer if values exist in sessionStorage
        const vetName = sessionStorage.getItem('whiteboardVetName');
        const clinicName = sessionStorage.getItem('whiteboardClinicName');

        if (vetName || clinicName) {
            setDisclaimerVetName(vetName || '');
            setDisclaimerClinicName(clinicName || '');
            setShowDisclaimer(true);

            // Clean up
            sessionStorage.removeItem('whiteboardVetName');
            sessionStorage.removeItem('whiteboardClinicName');
        }

        console.warn('[AutoLoad] Detected whiteboard to load:', parsed);

        const isDiagramVariant = (val) => ['withLabels', 'noLabels'].includes(val);
        if (isDiagramVariant(parsed.diagramVariant)) {
            setShowLabels(parsed.diagramVariant === 'withLabels');
        }

        setShowWhiteboard(true);

        if (parsed.whiteboardId) {
            applyWhiteboardColours(parsed.whiteboardId);
        }

        const waitForCanvasReady = (maxAttempts = 10, delay = 100) =>
            new Promise((resolve, reject) => {
                let attempts = 0;
                const check = () => {
                    const canvas = canvasRef.current;
                    if (canvas && canvas.getWidth() > 10 && canvas.getHeight() > 10) {
                        return resolve(canvas);
                    }

                    attempts++;
                    if (attempts >= maxAttempts) {
                        return reject('Canvas not ready in time');
                    }

                    setTimeout(check, delay);
                };
                check();
            });

        const loadWhiteboard = async () => {
            setIsAutoLoadingWhiteboard(true);
            try {
                const payload = {
                    contentCategory: 'whiteboard',
                    files: [{ method: 'get', key: parsed.canvasJsonLink }],
                };

                const presignedResp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                const presignedUrl = presignedResp.getUrls?.[0]?.presignedUrl;

                if (!presignedUrl) throw new Error('No presigned URL returned');

                const response = await fetch(presignedUrl);
                if (!response.ok) throw new Error('Failed to fetch whiteboard JSON');

                const jsonData = await response.json();

                await waitForCanvasReady(); // ensures canvas is fully mounted + sized

                canvasRef.current.loadFromJSON(jsonData, () => {
                    setTimeout(() => {
                        if (!canvasRef.current) return;

                        // Force a resize (in case dimensions changed after canvas mounted)
                        if (typeof canvasRef.current.doScaleAndResize === 'function') {
                            canvasRef.current.doScaleAndResize();
                        }

                        const oldW = jsonData.canvasWidth || 0;
                        const oldH = jsonData.canvasHeight || 0;
                        const newW = canvasRef.current.getWidth();
                        const newH = canvasRef.current.getHeight();

                        const objects = canvasRef.current.getObjects();

                        if (objects.length > 0 && oldW && oldH && newW && newH) {
                            scaleLoadedObjects(canvasRef.current, oldW, oldH, newW, newH);
                        }

                        canvasRef.current.requestRenderAll();
                        canvasRef.current.calcOffset();
                        window.dispatchEvent(new Event('resize'));

                        canvasRef.current.getObjects().forEach((obj) => {
                            obj.setCoords();
                        });

                        canvasRef.current.renderAll();
                        showSnackbar('Whiteboard successfully loaded!', 'success');
                    }, 600);
                });
            } catch (err) {
                console.error('Failed to auto-load whiteboard:', err);
            } finally {
                setIsAutoLoadingWhiteboard(false);
            }
        };

        loadWhiteboard();
    }, []);

    useEffect(() => {
        if (isTabletOrMobile || !showWhiteboard) return;
        const measure = () => {
            const el = document.querySelector('.handout-container');
            if (!el) return;
            const { top } = el.getBoundingClientRect();
            setToolbarTop(Math.round(top));
        };
        // measure after paint; run twice in case fonts/images shift layout
        requestAnimationFrame(measure);
        const t = setTimeout(measure, 250);
        return () => clearTimeout(t);
    }, [isTabletOrMobile, showWhiteboard]);

    const applyWhiteboardColours = (entryOrId) => {
        // 1) Use colours passed directly
        if (entryOrId?.lastUsedColours) {
            setLastUsedColours({
                pencil: entryOrId.lastUsedColours.pencil || [],
                spray: entryOrId.lastUsedColours.spray || [],
            });
            return true;
        }

        // 2) Try store
        const wbId = typeof entryOrId === 'string' ? entryOrId : entryOrId?.whiteboardId;
        const fromStore = wbId ? whiteboardStore?.whiteboardData?.[wbId] : null;

        if (fromStore?.lastUsedColours) {
            setLastUsedColours({
                pencil: fromStore.lastUsedColours.pencil || [],
                spray: fromStore.lastUsedColours.spray || [],
            });
            return true;
        }

        // 3) Fall back to user colours only if we haven't set anything yet
        setLastUsedColours((prev) => {
            const alreadySet = (prev?.pencil?.length || 0) > 0 || (prev?.spray?.length || 0) > 0;
            if (alreadySet) return prev;
            const seed = userDataStore?.lastUsedColours || userDataStore?.userData?.lastUsedColours || {};
            return { pencil: seed.pencil || [], spray: seed.spray || [] };
        });
        return false;
    };

    const updateRecentHandouts = (newContentId) => {
        const recentHandouts = JSON.parse(localStorage.getItem('recentHandouts')) || [];
        const updatedRecentHandouts = [newContentId, ...recentHandouts.filter((id) => id !== newContentId)].slice(
            0,
            MAX_RECENT_HANDOUTS,
        );
        localStorage.setItem('recentHandouts', JSON.stringify(updatedRecentHandouts));
        // console.log(`Updated recent handouts: ${updatedRecentHandouts}`);
    };

    const toggleAuthorsAndReferences = () => {
        setShowAuthorsAndReferences(!showAuthorsAndReferences);
        console.warn('change!', showAuthorsAndReferences);
    };

    if (!handoutData) {
        return (
            <Box sx={{ my: 2 }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    const handleSave = () => {
        setIsSaveModalOpen(true); // opens the modal when Save clicked
    };

    const handleLoad = async (entry) => {
        const vetName = `${entry.updatedByFirstName || ''} ${entry.updatedByLastName || ''}`.trim();
        const clinicName = userDataStore?.userData?.clinicName || '';

        setDisclaimerVetName(vetName);
        setDisclaimerClinicName(clinicName);
        setShowDisclaimer(true);

        applyWhiteboardColours(entry);
    };

    const openLoadModal = () => setIsLoadModalOpen(true);

    const handleReset = () => {
        if (canvasRef.current) {
            canvasRef.current.clear();
            localStorage.removeItem('lastWhiteboardState');
            console.warn('Whiteboard data cleared from localStorage by reset');
            setHasUnsavedChanges(false); //reset the state that there were any chnages (so no save dialog)
        }
    };

    // When user toggles Whiteboard ON/OFF
    const handleToggleWhiteboard = (event, newValue) => {
        // If turning ON, just proceed
        if (newValue) {
            setShowWhiteboard(true);
            if (!showMainIllustration) {
                setShowMainIllustration(true);
            }
            return;
        }

        // If turning OFF, prepare the action
        const turnOffWhiteboard = () => {
            localStorage.removeItem('lastWhiteboardState');
            setShowDisclaimer(false);
            setDisclaimerVetName('');
            setDisclaimerClinicName('');
            setShowWhiteboard(false);
        };

        // Use the enhanced context to prompt if needed
        promptBeforeAction(turnOffWhiteboard);
    };

    // After user saves, reset the unsaved state
    const handleWhiteboardSaved = () => {
        // for the dialog
        onCancelNavigation();
        setHasUnsavedChanges(false);
        setIsSaveModalOpen(false);

        // for disclaimer
        const vetName = `${userDataStore?.userData?.firstName || ''} ${userDataStore?.userData?.lastName || ''}`.trim();
        const clinicName = userDataStore?.userData?.clinicName || '';

        setDisclaimerVetName(vetName);
        setDisclaimerClinicName(clinicName);
        setShowDisclaimer(true);
    };

    const handleMainIllustrationToggle = (event, newValue) => {
        // If turning ON, just proceed
        if (newValue) {
            setShowMainIllustration(true);
            return;
        }

        // If turning OFF and whiteboard is active
        if (showWhiteboard) {
            const turnOffDiagramAndWhiteboard = () => {
                setShowMainIllustration(false);
                setShowWhiteboard(false);
            };

            // Use the context to prompt if there are unsaved changes
            promptUnsavedChanges(turnOffDiagramAndWhiteboard);
        } else {
            setShowMainIllustration(false);
        }
    };

    // rgb to hex
    const normalizeHex = (c) => {
        if (!c || typeof c !== 'string') return null;
        if (c.startsWith('rgba')) return null; // never store highlighter’s rgba
        if (c[0] === '#' && c.length === 4) {
            // #rgb → #rrggbb
            const r = c[1],
                g = c[2],
                b = c[3];
            return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
        }
        return c.toLowerCase();
    };

    const recordColourUse = (usedColour, tool) => {
        if (!['pencil', 'spray'].includes(tool)) return;

        const hex = normalizeHex(usedColour);
        if (!hex) return;

        setLastUsedColours((prev) => {
            const list = prev[tool] || [];
            const next = [hex, ...list.filter((x) => x.toLowerCase() !== hex)].slice(0, 6);
            return { ...prev, [tool]: next };
        });
    };

    return (
        <>
            {isAutoLoadingWhiteboard && <BlockingSpinner open={true} />}
            <HandoutBreadcrumb breadcrumbs={breadcrumbs} handoutTitle={handoutData.contentName} />
            {/* Add-ons Section for Mobile/Tablet */}
            {isTabletOrMobile && hasAddonsToShow && (
                <Box
                    sx={{
                        backgroundColor: 'white',
                        padding: 2,
                        paddingBottom: 0,
                        marginTop: 0,
                        marginBottom: 0,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: '#000453' }}>
                        Handout Add-ons
                    </Typography>
                    <FormGroup row>
                        {has3DModel && !is3DModelOnly && userDataStore?.subscriptionData?.accountType !== 'Student' && (
                            <FormControlLabel
                                control={
                                    <Switch checked={show3DModel} onChange={(e) => setShow3DModel(e.target.checked)} />
                                }
                                label={
                                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#000453' }}>
                                        3D model
                                    </Typography>
                                }
                            />
                        )}
                        {hasMainIllustration && !isDiagramOnly && (
                            <FormControlLabel
                                control={
                                    <Switch checked={showMainIllustration} onChange={handleMainIllustrationToggle} />
                                }
                                label={
                                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#000453' }}>
                                        Diagram
                                    </Typography>
                                }
                            />
                        )}
                        {hasTutorialVideo && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showTutorialVideo}
                                        onChange={(e) => setShowTutorialVideo(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#000453' }}>
                                        Tutorial Video
                                    </Typography>
                                }
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showWhiteboard}
                                    onChange={(event, checked) => {
                                        if (checked || !hasUnsavedChanges) {
                                            handleToggleWhiteboard(event, checked);
                                        } else {
                                            // For turning OFF with unsaved changes, prevent default
                                            event.preventDefault();
                                            handleToggleWhiteboard(event, checked);
                                        }
                                    }}
                                />
                            }
                            label="Whiteboard"
                        />
                    </FormGroup>
                </Box>
            )}
            {/* Mobile Toolbar (Above the Illustration) */}
            {isTabletOrMobile && showWhiteboard && (
                <Box
                    sx={{
                        width: '100%',
                        backgroundColor: 'white',
                        padding: 1,
                        zIndex: 1000,
                        position: 'sticky',
                        top: 0,
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        overflow: 'visible',
                    }}
                >
                    <WhiteboardToolbar
                        activeTool={activeTool}
                        setActiveTool={setActiveTool}
                        color={color}
                        setColor={setColor}
                        thickness={thickness}
                        setThickness={setThickness}
                        onSave={handleSave}
                        onLoad={openLoadModal}
                        onReset={handleReset}
                        hasUnsavedChanges={hasUnsavedChanges}
                        isExpanded={true}
                        setIsExpanded={() => {}}
                        recentColours={lastUsedColours[activeTool] || []}
                        onPickRecentColour={(c) => setColor(c)}
                        showTextHelp={false}
                    />
                </Box>
            )}
            <HandoutActions
                handoutData={handoutData}
                onToggleAuthorsAndReferences={toggleAuthorsAndReferences}
                showLabels={showLabels}
                show3DModel={show3DModel}
                showMainIllustration={showMainIllustration}
                showTutorialVideo={showTutorialVideo} // we do not have this yet
                isDiagramOnly={isDiagramOnly}
                is3DModelOnly={is3DModelOnly}
                showWhiteboard={showWhiteboard}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: hasAddonsToShow ? 'space-between' : 'center',
                    mt: isTabletOrMobile ? 0 : 4,
                    position: 'relative',
                }}
            >
                {/* Toolbar - desktop */}
                {!isTabletOrMobile && showWhiteboard && (
                    <Box
                        className="whiteboard-toolbar-position"
                        sx={{
                            position: 'fixed',
                            top: toolbarTop != null ? `${toolbarTop}px` : 0,
                            left: '24px',
                            zIndex: 1000000,
                            // width: isToolbarExpanded ? '100%' : '260px',
                        }}
                    >
                        <WhiteboardToolbar
                            activeTool={activeTool}
                            setActiveTool={(tool) => {
                                setActiveTool(tool);
                                setIsToolbarExpanded(true); // expand on tool click
                            }}
                            color={activeColor}
                            setColor={setColor}
                            thickness={thickness}
                            setThickness={setThickness}
                            onSave={handleSave}
                            onLoad={openLoadModal}
                            onReset={handleReset}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isExpanded={isToolbarExpanded}
                            setIsExpanded={setIsToolbarExpanded}
                            recentColours={lastUsedColours[activeTool] || []}
                            onPickRecentColour={(c) => setColor(c)}
                            showTextHelp={true}
                        />
                    </Box>
                )}

                <SaveWhiteboardModal
                    open={isSaveModalOpen}
                    onClose={() => setIsSaveModalOpen(false)}
                    canvasRef={canvasRef}
                    handoutData={handoutData}
                    // Pass to know when the user has actually saved
                    onSaved={handleWhiteboardSaved}
                    hasUnsavedChanges={hasUnsavedChanges}
                    showLabels={showLabels}
                    lastUsedColours={lastUsedColours}
                />

                <LoadWhiteboardModal
                    open={isLoadModalOpen}
                    onClose={() => setIsLoadModalOpen(false)}
                    onOpenFile={handleLoad}
                    handoutData={handoutData}
                    canvasRef={canvasRef}
                    onSetShowLabels={(val) => setShowLabels(val)}
                />

                {/* Left Spacer */}
                {!isTabletOrMobile && hasAddonsToShow && (
                    <Box
                        sx={{
                            width: '250px', // Same width as Handout Add-ons
                            mr: 2,
                            // Left spacer to balance the Handout Add-ons on the right
                        }}
                    />
                )}

                {/* Handout Content */}
                <Container
                    component={'section'}
                    maxWidth="md"
                    sx={{
                        width: '100%',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        // Restore original margins
                        // px: 3,
                        // py: 2,
                    }}
                    className="handout-container"
                >
                    <Box sx={{ my: 2 }} component={'section'} ref={handoutTopRef}>
                        {showAuthorsAndReferences ? (
                            <AuthorsAndReferences
                                handoutData={handoutData}
                                onToggleAuthorsAndReferences={toggleAuthorsAndReferences}
                            />
                        ) : (
                            <DisplayHandout
                                handoutData={handoutData}
                                showLabels={showLabels}
                                setShowLabels={setShowLabels}
                                show3DModel={show3DModel}
                                showMainIllustration={showMainIllustration}
                                showTutorialVideo={showTutorialVideo}
                                showWhiteboard={showWhiteboard}
                                activeTool={activeTool}
                                color={color}
                                thickness={thickness}
                                canvasRef={canvasRef}
                                onUnsavedChanges={setHasUnsavedChanges}
                                setToolbarExpanded={isTabletOrMobile ? () => {} : setIsToolbarExpanded}
                                showDisclaimer={
                                    showDisclaimer && userDataStore?.subscriptionData?.accountType !== 'Student'
                                }
                                vetName={disclaimerVetName}
                                clinicName={disclaimerClinicName}
                                onColorUsed={recordColourUse}
                            />
                        )}
                    </Box>
                </Container>
                {/* Handout Add-ons Section for Desktop */}
                {!isTabletOrMobile && hasAddonsToShow && (
                    <Box
                        sx={{
                            width: '250px',
                            ml: 2,
                            padding: 2,
                            // backgroundColor: 'transparent',
                        }}
                    >
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                whiteSpace: 'nowrap', // Prevent text from wrapping
                            }}
                        >
                            Handout Add-ons
                        </Typography>
                        <FormGroup
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    color: '#000453',
                                },
                            }}
                        >
                            {has3DModel &&
                                !is3DModelOnly &&
                                userDataStore?.subscriptionData?.accountType !== 'Student' && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={show3DModel}
                                                onChange={(e) => setShow3DModel(e.target.checked)}
                                            />
                                        }
                                        label="3D model"
                                    />
                                )}
                            {hasMainIllustration && !isDiagramOnly && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showMainIllustration}
                                            onChange={handleMainIllustrationToggle}
                                        />
                                    }
                                    label="Diagram"
                                />
                            )}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showWhiteboard}
                                        onChange={(event, checked) => {
                                            if (checked || !hasUnsavedChanges) {
                                                handleToggleWhiteboard(event, checked);
                                            } else {
                                                // For turning OFF with unsaved changes, prevent default
                                                event.preventDefault();
                                                handleToggleWhiteboard(event, checked);
                                            }
                                        }}
                                    />
                                }
                                label="Whiteboard"
                            />
                            {hasTutorialVideo && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showTutorialVideo}
                                            onChange={(e) => setShowTutorialVideo(e.target.checked)}
                                        />
                                    }
                                    label="Tutorial video"
                                />
                            )}
                        </FormGroup>
                    </Box>
                )}
            </Box>
        </>
    );
}

export default HandoutViewer;
